import React, { useState, useEffect } from "react";
import { VsiH4, VsiHr, VsiLgText } from "../../styled/App.styled";
import {
  Input,
  Label,
  Row,
  Col,
  FormGroup,
  Form,
  Button,
  FormFeedback,
  Spinner
} from "reactstrap";
import { useTitleFromComponent } from "../../utils/hooks";
import { useVsiAppContext } from "../../stores/VsiApp";
import { roles } from "../../utils/utils";
import { GetAuth, PostAuth } from "../../utils/Verbs";
import { capitalizeName } from "../../utils/Helpers";

const defaultValids = {
  firstName: true,
  lastName: true,
  email: true,
  company: true,
  uiTimeout: true
};

const selectAClient = { clientId: 0, name: "Select a Client" };

export default function AddUser({ context, title }) {
  useTitleFromComponent(`${context} / ${title}`);
  const { theme, user, getAccessTokenSilently } = useVsiAppContext();
  const [allClients, setAllClients] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [userType, setUserType] = useState(roles[0]);
  const [company, setCompany] = useState({
    ...user.client,
    label: user?.client?.name
  });
  const [uiTimeout, setUiTimeout] = useState(30);

  const [validInputs, setValidInputs] = useState(defaultValids);
  const [validCheck, setValidCheck] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);

  const [error, setError] = useState(null);

  const setDefaultState = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setUserType(roles[0]);
    setUiTimeout(30);
    setValidInputs(defaultValids);
    setValidCheck(false);
    setError(null);
  };

  const onChange = e => {
    const { name, value } = e.target;
    switch (name.toLowerCase()) {
      case "firstname":
        setFirstName(value);
        break;
      case "lastname":
        setLastName(value);
        break;
      case "email":
        setEmail(value);
        break;
      case "uitimeout":
        setUiTimeout(value);
        break;
      default:
      // do nothing
    }

    if (validCheck && !validInputs[name]) checkInputValidity();
    if (isSuccessful) setIsSuccessful(false);
  };

  const onTypeChange = e => {
    const { value } = e.target;
    const thisType = roles.find(perm => perm.label === value);
    setUserType(thisType || roles[0]);
  };

  const onCompanyChange = e => {
    const { value } = e.target;
    const thisClient = allClients.find(comp => comp.name === value);
    setCompany(thisClient);
    if (validCheck && !validInputs.company) checkInputValidity();
  };

  const checkInputValidity = () => {
    let newValids = { ...validInputs };

    for (let key in newValids) {
      switch (key.toLowerCase()) {
        case "firstname":
          newValids[key] = Boolean(firstName.match(/^[a-z ,.'-]+$/i));
          break;
        case "lastname":
          newValids[key] = Boolean(lastName.match(/^[a-z ,.'-]+$/i));
          break;
        case "email":
          newValids[key] = Boolean(email.match(/^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/));
          break;
        case "uitimeout":
          newValids[key] = uiTimeout >= 15 && uiTimeout <= 60;
          break;
        case "company":
          newValids[key] = Boolean(company.clientId !== 0);
          break;
        default:
        // do nothing
      }
    }

    setValidInputs(newValids);
    setValidCheck(true);
    return Object.keys(newValids).every(key => newValids[key]);
  };

  const isValid = name => {
    if (!validCheck) return null;
    else if (name && !validInputs[name]) return !validInputs[name];
    return null;
  };

  const submitForm = async e => {
    e.preventDefault();
    const areAllInputsValid = checkInputValidity();
    const token = await getAccessTokenSilently();
    if (areAllInputsValid) {
      const newUser = {
        salutation: null,
        firstName: capitalizeName(firstName.trim()),
        lastName: capitalizeName(lastName.trim()),
        name: `${capitalizeName(firstName.trim())} ${capitalizeName(lastName.trim())}`,
        email: email.toLowerCase(),
        client: company._id,
        phone: "123-123-123",
        clientId: company.clientId,
        lastLogin: Date.UTC(),
        companyName: company.label,
        role: userType.value,
        uiTimeout: uiTimeout
      };
      try {
        setIsPosting(true);
        let createUser;
        if (user.role === "local_admin") {
          createUser = await PostAuth("/api/v6/admin/users-admin", newUser, token);
        } else {
          createUser = await PostAuth("/api/v6/admin/users", newUser, token);
        }
        if (createUser) {
          setDefaultState();
          setIsSuccessful(true);
        }
        setIsPosting(false);
      } catch (err) {
        console.error(err);
        setError(err.response.data.message);
        setIsPosting(false);
      }
    }
  };

  useEffect(() => {
    const getAllRoles = async () => {
      if (user.role === "local_admin") {
        setAvailableRoles([
          {
            value: "basic_user",
            label: "Basic User"
          },
          {
            value: "local_admin",
            label: "Local Administrator"
          }
        ]);
      } else {
        setAvailableRoles(roles);
      }

      if (user.role === "super_user") {
        const token = await getAccessTokenSilently();
        GetAuth("/api/v6/admin/clients", token)
          .then(data => {
            setAllClients([selectAClient, ...data.data]);
          });
      } else {
        setAllClients([selectAClient, user.client]);
      }
    };
    getAllRoles()
  }, []);

  return <div>
    <VsiH4>Add User</VsiH4>
    <VsiHr />
    <Row>
      <Col xl={4} lg={5} md={12}>
        <Form onSubmit={submitForm}>
          <FormGroup>
            <Label for={"firstName"} className={`label-${theme}`}>
              First Name
            </Label>
            <Input id={"firstName"}
                   name={"firstName"}
                   invalid={isValid("firstName")}
                   placeholder={"VSI"}
                   value={firstName}
                   onChange={onChange} />
            <FormFeedback>
              This is a required field and must be a valid name
            </FormFeedback>
          </FormGroup>
          <FormGroup>
            <Label for={"email"} className={`label-${theme}`}>
              Last Name
            </Label>
            <Input id={"lastName"}
                   name={"lastName"}
                   placeholder={"User"}
                   invalid={isValid("lastName")}
                   value={lastName}
                   onChange={onChange} />
            <FormFeedback>
              This is a required field and must be a valid name
            </FormFeedback>
          </FormGroup>
          <FormGroup>
            <Label for={"email"} className={`label-${theme}`}>
              Email
            </Label>
            <Input id={"email"}
                   name={"email"}
                   type={"email"}
                   placeholder={"email@domain.com"}
                   value={email}
                   invalid={isValid("email")}
                   onChange={onChange} />
            <FormFeedback>
              This is a required field and must be a valid email address
            </FormFeedback>
          </FormGroup>
          <FormGroup>
            <Label for={"userType"} className={`label-${theme}`}>
              User Type
            </Label>
            <Input id={"userType"} placeholder={"Normal User"} type={"select"} value={userType.label}
                   onChange={onTypeChange}>
              {availableRoles.map((permission) => (
                <option key={`permission-${permission.value}`}>
                  {permission.label}
                </option>
              ))}
            </Input>
          </FormGroup>
          {user.role === "super_user" ?
            <FormGroup>
              <Label for={"company"} className={`label-${theme}`}>
                Company
              </Label>
              <Input id={"company"}
                     type={"select"}
                     value={company.label}
                     onChange={onCompanyChange}
                     invalid={isValid("company")}
                     placeholder={"Visual One Intelligence"}>
                {allClients.map((client, index) => (
                  <option key={`company-${index}`}>
                    {client.name}
                  </option>
                ))}
              </Input>
              <FormFeedback>
                This is a required field
              </FormFeedback>
            </FormGroup>
            : null}
          <FormGroup>
            <Label for={"uiTimeout"} className={`label-${theme}`}>
              UI Timeout (minutes)
            </Label>
            <Input id={"uiTimeout"}
                   name={"uiTimeout"}
                   type={"number"}
                   value={uiTimeout}
                   invalid={isValid("uiTimeout")}
                   onChange={onChange} />
            <FormFeedback>
              UI Timeout may not be less than 15 minutes and no greater than 1 hour
            </FormFeedback>
          </FormGroup>
          <Button type={"submit"} disabled={isPosting}>
            {isPosting ? <Spinner size={"sm"} /> : "Add User"}
          </Button>
          {isSuccessful ? <VsiLgText className={"ms-2"}>User added successfully!</VsiLgText> : null}
          {error ? <VsiLgText className={"ms-2"} style={{ color: "#dc3545" }}>{error}</VsiLgText> : null}
        </Form>
      </Col>
    </Row>
  </div>;
}
